import React from "react";
import { Box } from "@material-ui/core";
import { injectIntl, defineMessages } from "react-intl";
import { Table } from "antd";
import { UploadFileResumeErrors } from "./UploadFileResumeErrors";
import TemplateDownloadModal from "../modals/TemplateDownloadModal/TemplateDownloadModal";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CustomAlertIcon,
} from "../../../common/icons";
import CustomPaginator from "../../../helpers/customPaginator";
import HUBContext from "../../../services/HUBContext";

const messages = defineMessages({
  title: {
    id: "vehicle.single.registry.upload.resume.title",
    defaultMessage: "Review your additions and changes",
  },
  subtitle: {
    id: "vehicle.single.registry.upload.resume.subtitle",
    defaultMessage:
      "The data has not been applied yet. Please review the information to proceed or upload a new file with the necessary corrections.",
  },
  tableColumnPlate: {
    id: "vehicle.single.registry.upload.resume.tableColumnPlate",
    defaultMessage: "Plate",
  },
  tableColumnStatus: {
    id: "vehicle.single.registry.upload.resume.tableColumnStatus",
    defaultMessage: "Status",
  },
  tableColumnVehicleStatus: {
    id: "vehicle.single.registry.upload.resume.tableColumnVehicleStatus",
    defaultMessage: "Vehicle Status",
  },
  tableColumnModel: {
    id: "vehicle.single.registry.upload.resume.tableColumnModel",
    defaultMessage: "Model",
  },
  tableColumnYear: {
    id: "vehicle.single.registry.upload.resume.tableColumnYear",
    defaultMessage: "Year",
  },
});

export class UploadFileResume extends React.Component {
  state = {
    isTemplateModalOpen: false,
    pageIndex: 0,
    pageSize: 5,
  };

  countErrorsPerLine(dataTable) {
    if (!dataTable || !dataTable.data) {
      return 0;
    }

    const response = dataTable.data.filter((row) => {
      return (
        row.errors &&
        Object.values(row.errors).some(
          (error) => error && Object.keys(error).length > 0,
        )
      );
    });

    return response.length;
  }

  handleDownloadTemplate() {
    this.handleOpenTemplateModal();
  }

  handleOpenTemplateModal = () => {
    this.setState({ isTemplateModalOpen: true });
  };

  handleCloseTemplateModal = () => {
    this.setState({ isTemplateModalOpen: false });
  };

  handlePageChange = (page) => {
    this.setState({ pageIndex: page - 1 });
  };

  handleShowSizeChange = (current, size) => {
    this.setState({
      pageSize: size,
      pageIndex: 0,
    });
  };

  hasCompanyUploadId() {
    return (
      this.props.companyUploadId !== 0 || this.props.companyUploadId !== null
    );
  }

  render() {
    const { intl, companyId, dataTable } = this.props;

    this.countErrorsPerLine(dataTable);

    const { pageIndex, pageSize } = this.state;

    const allData = dataTable?.data || [];
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const currentData = allData.slice(startIndex, endIndex);

    const columns = [
      ...(this.countErrorsPerLine(dataTable) > 0
        ? [
            {
              title: "",
              key: "errorIcon",
              width: 60,
              render: (text, record) => {
                const hasErrors =
                  record.errors &&
                  Object.values(record.errors).some(
                    (error) => error && Object.keys(error).length > 0,
                  );

                return hasErrors ? <CustomAlertIcon /> : null;
              },
            },
          ]
        : []),
      {
        title: intl.formatMessage(messages.tableColumnPlate),
        key: "plate",
        render: (text, record) =>
          record?.data?.plate ? record.data.plate : <CustomAlertIcon />,
      },
      {
        title: intl.formatMessage(messages.tableColumnStatus),
        key: "vehicleStatusID",
        render: (text, record, _) => {
          const statusClassName =
            record.data?.vehicleStatusID === 2
              ? "disabled"
              : record.data?.vehicleStatusID === 0
                ? "canceled"
                : "active";

          return record.data?.vehicleStatusID !== null ? (
            <span className={`v3-tag ${statusClassName}`}>
              {intl.formatMessage({
                id: `vehicles.vehicleStatusID.${text.data.vehicleStatusID}`,
                defaultMessage: "N/A",
              })}
            </span>
          ) : (
            <CustomAlertIcon />
          );
        },
        sorter: (a, b) => a.data.vehicleStatusID - b.data.vehicleStatusID,
      },
      {
        title: intl.formatMessage(messages.tableColumnVehicleStatus),
        key: "ownershipStatusID",
        render: (text, record, _) => {
          var statusMapped = {
            1: "Active",
            2: "Inactive",
            3: "disabeld",
          };
          const statusClassName =
            statusMapped[record.data?.ownershipStatusID] ?? statusMapped[3];
          let children = null;

          if (
            this.hasCompanyUploadId() &&
            record?.data?.ownershipStatusID !== null
          )
            children = (
              <span className={`v3-tag ${statusClassName}`}>
                {intl.formatMessage({
                  id: `vehicle.single.registry.upload.vehicleStatus.${text.data.ownershipStatusID}`,
                  defaultMessage: "N/A",
                })}
              </span>
            );

          if (
            this.hasCompanyUploadId() &&
            record?.data?.ownershipStatusID === null
          )
            children = <span className={`v3-tag disabeld`}>N/A</span>;

          return {
            children,
          };
        },
        sorter: (a, b) =>
          (a.data.ownershipStatusID || 0) -
          (b.data.statusVehownershipStatusIDicleID || 0),
      },
      {
        title: intl.formatMessage(messages.tableColumnModel),
        key: "vehicleModel",
        render: (_text, record) => {
          let children = <CustomAlertIcon />;

          if (this.hasCompanyUploadId() && record?.data?.vehicleModel !== null)
            children = (
              <>
                <h5>{record.data.vehicleModel}</h5>
                <span>{record.data?.vehicleBrand}</span>
              </>
            );

          if (this.hasCompanyUploadId() && record?.data?.vehicleModel === null)
            children = <span className={`v3-tag disabeld`}>N/A</span>;

          return {
            children: (
              <span className="header-muted-text-cell">{children}</span>
            ),
          };
        },
      },
      {
        title: intl.formatMessage(messages.tableColumnYear),
        key: "modelYear",
        render: (_, record) => {
          let children = <CustomAlertIcon />;

          if (this.hasCompanyUploadId() && record?.data?.modelYear !== null)
            children = <>{record.data.modelYear}</>;

          if (this.hasCompanyUploadId() && record?.data?.modelYear === null)
            children = (
              <span className="header-muted-text-cell">
                <span className={`v3-tag disabeld`}>N/A</span>
              </span>
            );

          return {
            children,
          };
        },
        sorter: (a, b) => a.data.modelYear - b.data.modelYear,
      },
      ...(this.countErrorsPerLine(dataTable) > 0
        ? [
            {
              title: "",
              key: "action",
              width: 50,
              render: () => <span>&nbsp;</span>,
            },
          ]
        : []),
    ];

    return (
      <Box>
        <div style={{ textAlign: "start", marginBottom: "1rem" }}>
          <h1 style={{ fontWeight: "500", fontSize: "20px" }}>
            {intl.formatMessage(messages.title)}
          </h1>
          <h3 style={{ fontWeight: "400", fontSize: "16px" }}>
            {intl.formatMessage(messages.subtitle)}
          </h3>
        </div>
        {this.countErrorsPerLine(dataTable) > 0 ? (
          <UploadFileResumeErrors
            count={this.countErrorsPerLine(dataTable)}
            onDownloadClick={this.handleDownloadTemplate.bind(this)}
          />
        ) : null}

        <div className="card">
          <div className="content-card">
            <div className="table-area">
              <Table
                pagination={false}
                scroll={{ x: 400 }}
                dataSource={currentData}
                columns={columns}
                rowKey={(record) => record.data.plate}
                expandedRowRender={(record) => {
                  if (!record.errors) {
                    return null;
                  }
                  const validErrors = Object.entries(record.errors).filter(
                    ([, errorDetails]) =>
                      errorDetails && Object.keys(errorDetails).length > 0,
                  );

                  if (validErrors.length === 0) {
                    return null;
                  }
                  return (
                    <div>
                      <p
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#33323D",
                        }}
                      >
                        {validErrors.length === 1
                          ? `${validErrors.length} erro detectado:`
                          : `${validErrors.length} erros detectados:`}
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, minmax(200px, 1fr))",
                          marginTop: "20px",
                          gap: "24px",
                        }}
                      >
                        {validErrors.map(([tab, errosTab]) => {
                          const errorColumnsList =
                            Object.values(errosTab).join(", ");

                          return (
                            <div key={tab}>
                              <p
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "400",
                                  color: "#6C7786",
                                  marginBottom: "4px",
                                }}
                              >
                                Aba {tab}
                              </p>
                              <p
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  color: "#1E293B",
                                  whiteSpace: "normal",
                                  overflowWrap: "break-word",
                                  lineHeight: "1.5",
                                }}
                              >
                                <span style={{ fontWeight: "700" }}>
                                  Erros:
                                </span>{" "}
                                {errorColumnsList}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }}
                expandIconAsCell={false}
                expandIconColumnIndex={
                  this.countErrorsPerLine(dataTable) > 0
                    ? columns.length - 1
                    : null
                }
                expandIcon={({ expanded, onExpand, record }) => {
                  const hasErrors =
                    record.errors &&
                    Object.values(record.errors).some(
                      (error) => error && Object.keys(error).length > 0,
                    );
                  return (
                    <span
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        cursor: "pointer",
                        fontSize: "18px",
                        display: "inline-block",
                      }}
                    >
                      {hasErrors &&
                        (expanded ? <ArrowUpIcon /> : <ArrowDownIcon />)}
                    </span>
                  );
                }}
              />
              <CustomPaginator
                pagerInfo={{
                  pageIndex: pageIndex,
                  pageSize: pageSize,
                  count: allData.length,
                }}
                onChange={this.handlePageChange}
                onShowSizeChange={this.handleShowSizeChange}
                pageSizeOptions={["5", "10", "20", "30"]}
              />
            </div>
          </div>
          <div className="footer-card" />
        </div>
        <TemplateDownloadModal
          locale={this.context.locale}
          open={this.state.isTemplateModalOpen}
          onClose={this.handleCloseTemplateModal}
          companyId={companyId}
          integratorsSolutionsCode={this.props.integratorsSolutionsCode}
        />
      </Box>
    );
  }
}
UploadFileResume.contextType = HUBContext;
export default injectIntl(UploadFileResume);
